import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BulkSetStockInput,
  BulkSetStockResult,
  GodownStockMatrixResponse,
  InactiveProductStockRow,
  MultiGodownStockResponse,
  OrgNodeItem,
  Paginated,
  PaginationQuery,
  PostAdjustmentInput,
  PostOpeningStockInput,
  StockBalanceItem,
  StockCheckItem,
  ProductStockHoldDetails,
  StockMovementItem,
  StockVerificationFilterOptions,
  StockVerificationReportRow,
  SixOrbitStockExportRow,
} from '@tiles-erp/shared-types';
import { apiFetch } from '../../lib/api-client';

const KEY = 'stock';

export interface StockFilters {
  branchId?: string;
  godownId?: string;
  brandId?: string;
  categoryId?: string;
  sizeMm?: string;
  piecesPerBox?: string;
  batchNo?: string;
  shade?: string;
  /** Merge batch/shade rows into one line per product and godown. */
  groupByProduct?: string;
  productId?: string;
  fromDate?: string;
  toDate?: string;
  showZeroStock?: string;
}

export interface StockVerificationFilters extends StockFilters {
  checkedBy?: string;
  result?: string;
}

const withFilters = (params: URLSearchParams, filters: StockFilters): URLSearchParams => {
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  return params;
};

export function useStockBalances(query: PaginationQuery, filters: StockFilters, enabled: boolean) {
  const params = withFilters(
    new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) }),
    filters,
  );
  if (query.search) params.set('search', query.search);
  return useQuery({
    queryKey: [KEY, 'balances', query, filters],
    queryFn: () => apiFetch<Paginated<StockBalanceItem>>(`/stock/balances?${params.toString()}`),
    enabled,
  });
}

export function useGodownStockMatrix(query: PaginationQuery, filters: StockFilters, enabled = true) {
  const params = withFilters(
    new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) }),
    filters,
  );
  if (query.search) params.set('search', query.search);
  return useQuery({
    enabled,
    queryKey: [KEY, 'godown-matrix', query, filters],
    queryFn: () => apiFetch<GodownStockMatrixResponse>(`/stock/godown-matrix?${params.toString()}`),
    placeholderData: (previous) => previous,
  });
}

export function useInactiveProductStock(query: PaginationQuery, filters: StockFilters) {
  const params = withFilters(
    new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) }), filters,
  );
  if (query.search) params.set('search', query.search);
  return useQuery({
    queryKey: [KEY, 'inactive-products-with-stock', query, filters],
    queryFn: () => apiFetch<Paginated<InactiveProductStockRow>>(`/stock/inactive-products-with-stock?${params.toString()}`),
    placeholderData: (previous) => previous,
  });
}

export function useMultiGodownStock(query: PaginationQuery, filters: StockFilters, enabled: boolean) {
  const params = withFilters(
    new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) }),
    filters,
  );
  if (query.search) params.set('search', query.search);
  return useQuery({
    queryKey: [KEY, 'multiple-godowns', query, filters],
    queryFn: () => apiFetch<MultiGodownStockResponse>(`/stock/multiple-godowns?${params.toString()}`),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useSixOrbitStockExport(query: PaginationQuery, filters: StockFilters) {
  const params = withFilters(
    new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) }),
    filters,
  );
  if (query.search) params.set('search', query.search);
  return useQuery({
    queryKey: [KEY, 'sixorbit-export', query, filters],
    queryFn: () => apiFetch<Paginated<SixOrbitStockExportRow>>(`/stock/sixorbit-export?${params.toString()}`),
    placeholderData: (previous) => previous,
  });
}

export function useStockVerificationReport(query: PaginationQuery, filters: StockVerificationFilters) {
  const params = withFilters(
    new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) }),
    filters,
  );
  if (query.search) params.set('search', query.search);
  return useQuery({
    queryKey: [KEY, 'verification-report', query, filters],
    queryFn: () => apiFetch<Paginated<StockVerificationReportRow>>(`/stock/verification-report?${params.toString()}`),
    placeholderData: (previous) => previous,
  });
}

export function useStockVerificationFilterOptions() {
  return useQuery({
    queryKey: [KEY, 'verification-report', 'options'],
    queryFn: () => apiFetch<StockVerificationFilterOptions>('/stock/verification-report/options'),
  });
}

export function useStockCheck(query: PaginationQuery, branchId: string, enabled: boolean) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
    branchId,
  });
  if (query.search) params.set('search', query.search);
  return useQuery({
    queryKey: [KEY, 'check', query, branchId],
    queryFn: () => apiFetch<Paginated<StockCheckItem>>(`/stock/check?${params.toString()}`),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useProductStockCheck(productId: string) {
  return useQuery({
    queryKey: [KEY, 'product-check', productId],
    queryFn: () => apiFetch<StockCheckItem[]>(`/stock/check/product/${productId}`),
    enabled: Boolean(productId),
  });
}

/** Count sheet: every product for a godown, including those with no stock yet. */
export function useCountSheet(
  branchId: string | undefined,
  godownId: string | undefined,
  query: PaginationQuery,
  filters: StockFilters,
  enabled: boolean,
) {
  const params = withFilters(
    new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) }),
    { ...filters, branchId, godownId },
  );
  if (query.search) params.set('search', query.search);
  return useQuery({
    queryKey: [KEY, 'count-sheet', branchId ?? null, godownId ?? null, query, filters],
    queryFn: () => apiFetch<Paginated<StockBalanceItem>>(`/stock/count-sheet?${params.toString()}`),
    enabled,
  });
}

export function useStockMovements(
  query: PaginationQuery,
  filters: StockFilters & { type?: string },
  enabled: boolean,
) {
  const params = withFilters(
    new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) }),
    filters,
  );
  if (filters.type) params.set('type', filters.type);
  if (query.search) params.set('search', query.search);
  return useQuery({
    queryKey: [KEY, 'movements', query, filters],
    queryFn: () => apiFetch<Paginated<StockMovementItem>>(`/stock/movements?${params.toString()}`),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useProductActivity(
  query: PaginationQuery,
  filters: StockFilters,
  enabled: boolean,
) {
  return useStockMovements(query, filters, enabled);
}

export function useGodowns(branchId?: string) {
  return useQuery({
    queryKey: ['/godowns', 'options', branchId ?? null],
    queryFn: () =>
      apiFetch<Paginated<OrgNodeItem>>(
        `/godowns?page=1&pageSize=200${branchId ? `&parentId=${branchId}` : ''}`,
      ),
    staleTime: 60_000,
    select: (data) => data.items,
  });
}

export function usePostOpeningStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PostOpeningStockInput) =>
      apiFetch<{ posted: number }>('/stock/opening', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function usePostAdjustment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PostAdjustmentInput) =>
      apiFetch<{ posted: number }>('/stock/adjustments', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useBulkSetStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkSetStockInput) =>
      apiFetch<BulkSetStockResult>('/stock/count', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

export interface SmartStockCheckItem {
  productId: string;
  sku: string;
  productName: string;
  brandName: string;
  sizeMm: string | null;
  piecesPerBox: number;
  baseUom: string;
  bookQtyBoxes: number;
  lastCheckedAt: string | null;
  lastCountedQtyBoxes: number | null;
  lastDifferenceBoxes: number | null;
  lastMovementAt: string | null;
  lastBilledAt: string | null;
  due: boolean;
}

export interface SmartStockCheckResponse {
  items: SmartStockCheckItem[];
  totalProducts: number;
  dueProducts: number;
}

export function useSmartStockCheck(branchId: string, intervalMinutes: number, search: string, limit: number, billedDate: string) {
  const params = new URLSearchParams({ branchId, intervalMinutes: String(intervalMinutes), limit: String(limit), billedDate });
  if (search.trim()) params.set('search', search.trim());
  return useQuery({
    queryKey: [KEY, 'smart-check', branchId, intervalMinutes, search, limit, billedDate],
    queryFn: () => apiFetch<SmartStockCheckResponse>(`/stock/smart-check?${params.toString()}`),
    enabled: Boolean(branchId),
    refetchInterval: 30_000,
  });
}

export function useVerifySmartStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { branchId: string; productId: string }) =>
      apiFetch<{ bookQtyBoxes: number }>('/stock/smart-check', {
        method: 'POST', body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useProductStockHolds(productId:string,branchId:string) {
  return useQuery({queryKey:[KEY,'product-holds',productId,branchId],
    queryFn:()=>apiFetch<ProductStockHoldDetails>(`/stock/check/product/${productId}/reservations/${branchId}`),
    enabled:Boolean(productId&&branchId),staleTime:0});
}
